import Layout from "@/components/Layout";
import { formatCurrency } from "@/lib/currencies/currenciesStore";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ProductPostingAdDialog, type ProductPostingPlatform } from "@/components/catalog/ProductPostingAdDialog";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { isAxiosError } from "axios";
import { MODEL_YEARS_DESC } from "@/components/catalog/VehicleProductNameModal";
import { api } from "@/lib/apiClient";
import { listBrandRecords } from "@/lib/catalog/brandsStore";
import { listCategories } from "@/lib/catalog/mockCatalogApi";
import { computeLandedCost } from "@/lib/catalog/purchaseCost";
import {
  catalogProductErrorMessage,
  changeProductInventoryStatus,
  deleteProduct,
  copyProductAsDraft,
  fetchProductsCatalogPage,
  getProductById,
  publishProductToInStock,
  type FetchProductsCatalogParams,
  type InventoryStatusValue,
  type ProductRecord,
} from "@/lib/catalog/productsStore";
import {
  buildMarketplaceAdPostPayload,
  productPostingPlatformToApi,
  type MarketplaceAdPostPayload,
} from "@/lib/catalog/marketplaceAdPostPayload";
import {
  createProductAdListing,
  fetchProductAdListings,
  updateProductAdListing,
  type ProductAdListingRow,
} from "@/lib/catalog/productAdListingsStore";
import { buildCategoryProductSchema } from "@/lib/catalog/buildCategorySchema";
import { syncCatalogAttributesFromApi } from "@/lib/catalog/catalogAttributesStore";
import { syncLinksForCategoryFromApi } from "@/lib/catalog/categoryAttributeLinksStore";
import type { Attribute, AttributeValue, Category } from "@/lib/catalog/types";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { OlxIcon } from "@/components/icons/OlxIcon";
import { PakWheelsIcon } from "@/components/icons/PakWheelsIcon";
import { WiseWheelsIcon } from "@/components/icons/WiseWheelsIcon";
import {
  Car,
  CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Copy,
  Edit,
  Eye,
  Filter,
  LayoutGrid,
  List,
  Loader2,
  Megaphone,
  Package,
  Plus,
  Receipt,
  RefreshCw,
  Search,
  Send,
  Trash2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import PermissionDenied from "@/components/PermissionDenied";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { motion } from "framer-motion";
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

function extractApiDataArray<T>(body: unknown): T[] {
  if (!body || typeof body !== "object") return [];
  const data = (body as Record<string, unknown>).data;
  return Array.isArray(data) ? (data as T[]) : [];
}

const INVENTORY_STATUS_FILTER_OPTS = [
  { value: "draft", label: "Draft" },
  { value: "in_stock", label: "In stock" },
  { value: "reserved", label: "Reserved" },
  { value: "unsold", label: "Unsold" },
  { value: "sold", label: "Sold" },
] as const;

function optionLabel(attribute: Attribute, raw: string): string {
  const opt = attribute.options?.find((o) => o.value === raw);
  return opt?.label ?? raw;
}

function formatListingValue(attribute: Attribute, value: AttributeValue | undefined): string {
  if (value == null) return "—";
  if (attribute.type === "select") {
    const raw = typeof value === "string" ? value : "";
    return raw ? optionLabel(attribute, raw) : "—";
  }
  if (attribute.type === "multiselect") {
    const raws = Array.isArray(value) ? (value as string[]) : [];
    if (!raws.length) return "—";
    return raws.map((v) => optionLabel(attribute, v)).join(", ");
  }
  if (Array.isArray(value)) return (value as unknown[]).map((v) => String(v)).join(", ");
  if (typeof value === "object") return "—";
  return String(value);
}

/** Values only, pipe-separated; hover shows the attribute name. */
function ProductListingAttrPipes({
  attributes,
  values,
  className,
}: {
  attributes: Attribute[];
  values: Record<string, AttributeValue> | undefined;
  className?: string;
}) {
  if (attributes.length === 0) return null;
  return (
    <div className={cn("flex flex-wrap items-center gap-x-0 text-xs text-muted-foreground", className)}>
      {attributes.map((a, i) => {
        const display = formatListingValue(a, values?.[a.code]);
        return (
          <span key={a.id} className="inline-flex items-center min-w-0">
            {i > 0 ? <span className="mx-1.5 text-muted-foreground/50 select-none" aria-hidden>|</span> : null}
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <span
                  className="truncate max-w-[9rem] cursor-default hover:text-foreground transition-colors"
                  tabIndex={0}
                >
                  {display}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">{a.name}</TooltipContent>
            </Tooltip>
          </span>
        );
      })}
    </div>
  );
}

/** High-contrast pill for product cards (readable on any photo background). */
function inventoryCardStatusPill(status: string | null | undefined): { label: string; className: string; dotClassName: string } {
  const s = status ?? "draft";
  if (s === "sold") {
    return {
      label: "Sold",
      className: "border border-rose-400/40 bg-rose-600/90 text-white shadow-md shadow-rose-950/20 backdrop-blur-md",
      dotClassName: "bg-rose-300",
    };
  }
  if (s === "reserved") {
    return {
      label: "Reserved",
      className: "border border-sky-400/40 bg-sky-600/90 text-white shadow-md shadow-sky-950/20 backdrop-blur-md",
      dotClassName: "bg-sky-300 animate-pulse",
    };
  }
  if (s === "draft") {
    return {
      label: "Draft",
      className: "border border-amber-400/50 bg-amber-500/95 text-amber-950 shadow-md shadow-amber-950/15 backdrop-blur-md font-black",
      dotClassName: "bg-amber-950",
    };
  }
  if (s === "unsold") {
    return {
      label: "Unsold",
      className: "border border-orange-400/40 bg-orange-600/90 text-white shadow-md shadow-orange-950/20 backdrop-blur-md",
      dotClassName: "bg-orange-300",
    };
  }
  return {
    label: "In Stock",
    className: "border border-emerald-400/40 bg-emerald-600/90 text-white shadow-md shadow-emerald-950/20 backdrop-blur-md",
    dotClassName: "bg-emerald-300 animate-pulse",
  };
}

function formatPKR(n: number | null | undefined, currencyOverride?: string): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return formatCurrency(Number(n), currencyOverride);
}

function formatProductCost(
  p: Pick<ProductRecord, "landedCost" | "purchasePrice" | "purchaseCostLines" | "purchaseCurrency">
): string {
  const landed = p.landedCost ?? computeLandedCost(p.purchasePrice, p.purchaseCostLines);
  const cost = landed ?? p.purchasePrice ?? null;
  return formatPKR(cost, p.purchaseCurrency || undefined);
}

function ProductCardPrices({
  product,
  compact = false,
}: {
  product: Pick<ProductRecord, "landedCost" | "purchasePrice" | "purchaseCostLines" | "purchaseCurrency" | "salePrice">;
  compact?: boolean;
}) {
  const costText = formatProductCost(product);
  const saleText = formatPKR(product.salePrice);

  if (compact) {
    return (
      <div className="flex items-center gap-x-3 gap-y-1">
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 cursor-help">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">Cost:</span>
              <span className="text-xs font-bold tabular-nums text-[#eb980a]">{costText}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top">Purchase Cost: {costText}</TooltipContent>
        </Tooltip>

        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1 cursor-help">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">Asking:</span>
              <span className="text-xs font-bold tabular-nums text-emerald-600 dark:text-emerald-400">{saleText}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="top">Asking Sale Price: {saleText}</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="flex items-baseline justify-between gap-2.5 mt-1">
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div className="min-w-0 cursor-help">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
              Cost
            </span>
            <p className="font-bold tabular-nums text-[#eb980a] truncate text-sm sm:text-base">
              {costText}
            </p>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">Purchase Cost: {costText}</TooltipContent>
      </Tooltip>

      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div className="shrink-0 text-right cursor-help">
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
              Asking
            </span>
            <p className="font-bold tabular-nums text-emerald-600 dark:text-emerald-400 text-sm sm:text-base">
              {saleText}
            </p>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">Asking Sale Price: {saleText}</TooltipContent>
      </Tooltip>
    </div>
  );
}

const PRODUCT_POSTING_PLATFORM_LABEL: Record<ProductPostingPlatform, string> = {
  olx: "OLX",
  pakwheels: "PakWheels",
  wisewheels: "Wise Wheels",
};

const AD_LISTING_PLATFORM_LABEL: Record<ProductAdListingRow["platform"], string> = {
  olx: "OLX",
  "pak-wheels": "PakWheels",
  "wise-wheels": "Wise Wheels",
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function AdListingPlatformIcon({
  platform,
  className,
}: {
  platform: ProductAdListingRow["platform"];
  className?: string;
}) {
  const cls = cn("shrink-0", className ?? "h-3 w-3");
  if (platform === "olx") {
    return <OlxIcon className={cn(cls, "text-emerald-600 dark:text-emerald-400")} aria-hidden />;
  }
  if (platform === "pak-wheels") {
    return <PakWheelsIcon className={cls} aria-hidden />;
  }
  return <WiseWheelsIcon className={cls} aria-hidden />;
}

function formatListingCreatedAt(iso: string): string {
  try {
    const d = parseISO(iso);
    return isValid(d) ? format(d, "PPp") : iso;
  } catch {
    return iso;
  }
}

/** Compact platform icons beside the product title; hover for listing details. */
function ProductHeadingMarketplaceIcons({
  listings,
  canViewPrices,
}: {
  listings: ProductAdListingRow[];
  canViewPrices: boolean;
}) {
  if (listings.length === 0) return null;
  return (
    <span className="inline-flex shrink-0 items-center gap-0.5" aria-label="Marketplace listings">
      {listings.map((row) => {
        const label = AD_LISTING_PLATFORM_LABEL[row.platform];
        const priceLine =
          canViewPrices && row.salePrice != null ? formatPKR(row.salePrice) : canViewPrices ? "—" : "Hidden (no price permission)";
        return (
          <Tooltip key={row.id} delayDuration={200}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border/70 bg-background/80 text-foreground shadow-sm transition-colors hover:bg-muted/80 hover:border-border"
                aria-label={`${label} listing — hover for details`}
              >
                <AdListingPlatformIcon platform={row.platform} className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              align="start"
              sideOffset={8}
              collisionPadding={16}
              className="max-w-[min(22rem,calc(100vw-2rem))] break-words px-3 py-2.5 text-left leading-relaxed"
            >
              <div className="space-y-1.5 text-xs leading-snug break-words">
                <p className="font-semibold text-foreground">{label}</p>
                <p>
                  <span className="text-muted-foreground">Status </span>
                  <span className="font-medium text-foreground">Posted</span>
                </p>
                <p>
                  <span className="text-muted-foreground">Created </span>
                  <span className="font-medium text-foreground tabular-nums">{formatListingCreatedAt(row.createdAt)}</span>
                </p>
                <p>
                  <span className="text-muted-foreground">Sale price </span>
                  <span className="font-medium text-foreground tabular-nums">{priceLine}</span>
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        );
      })}
    </span>
  );
}

function ProductMarketplaceMenuItems({
  product,
  onPost,
  iconSizeClassName,
}: {
  product: ProductRecord;
  onPost: (p: ProductRecord, platform: ProductPostingPlatform) => void;
  iconSizeClassName: string;
}) {
  return (
    <>
      <DropdownMenuItem className="gap-2" onClick={() => onPost(product, "olx")}>
        <OlxIcon className={cn(iconSizeClassName, "text-emerald-600 dark:text-emerald-400")} />
        OLX
      </DropdownMenuItem>
      <DropdownMenuItem className="gap-2" onClick={() => onPost(product, "pakwheels")}>
        <PakWheelsIcon className={iconSizeClassName} />
        PakWheels
      </DropdownMenuItem>
      <DropdownMenuItem className="gap-2" onClick={() => onPost(product, "wisewheels")}>
        <WiseWheelsIcon className={iconSizeClassName} />
        Wise Wheels
      </DropdownMenuItem>
    </>
  );
}

const PRODUCTS_PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

const Products = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canViewDetail = can(user, "product_can_view");
  const canCreate = can(user, "product_can_create");
  const canUpdate = can(user, "product_can_update");
  const canDelete = can(user, "product_can_delete");
  const canViewPrices = can(user, "product_can_view_prices");
  const canCreateSale = can(user, "sale_can_create") || can(user, "product_can_view_prices");
  const [isForbidden, setIsForbidden] = useState(false);

  const [categories, setCategories] = useState<Category[]>([]);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [statusModalProduct, setStatusModalProduct] = useState<ProductRecord | null>(null);
  const [nextInventoryStatus, setNextInventoryStatus] = useState<InventoryStatusValue | "">("");
  const [statusReason, setStatusReason] = useState("");
  const [statusSaving, setStatusSaving] = useState(false);
  const [publishConfirmProduct, setPublishConfirmProduct] = useState<ProductRecord | null>(null);
  const [publishBusyId, setPublishBusyId] = useState<string | null>(null);
  const [copyConfirmProduct, setCopyConfirmProduct] = useState<ProductRecord | null>(null);
  const [copyBusyId, setCopyBusyId] = useState<string | null>(null);
  const [copySections, setCopySections] = useState({
    basic: true,
    additional: true,
    media: true,
    purchase: true,
  });
  const [adPost, setAdPost] = useState<MarketplaceAdPostPayload | null>(null);
  const [lightbox, setLightbox] = useState<{ open: boolean; productId: string | null; index: number; zoom: number }>({
    open: false,
    productId: null,
    index: 0,
    zoom: 1,
  });
  const lightboxViewportRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ dragging: boolean; startX: number; startY: number; scrollLeft: number; scrollTop: number }>({
    dragging: false,
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [productAdListings, setProductAdListings] = useState<ProductAdListingRow[]>([]);

  const [filterCategoryId, setFilterCategoryId] = useState<string>("__all__");
  const [filterBrandId, setFilterBrandId] = useState<string>("__all__");
  const [statusInclusions, setStatusInclusions] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(INVENTORY_STATUS_FILTER_OPTS.map((o) => [o.value, true])),
  );

  useEffect(() => {
    const statusParam = searchParams.get("inventory_status");
    if (!statusParam) return;
    const codes = new Set(
      statusParam
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean),
    );
    if (codes.size === 0) return;
    setStatusInclusions(
      Object.fromEntries(INVENTORY_STATUS_FILTER_OPTS.map((o) => [o.value, codes.has(o.value)])),
    );
  }, [searchParams]);

  const [filterVehicleYear, setFilterVehicleYear] = useState<string>("__all__");
  const [filterVehicleBrandId, setFilterVehicleBrandId] = useState<string>("__all__");
  const [filterVehicleModelId, setFilterVehicleModelId] = useState<string>("__all__");
  const [filterVehicleVersionId, setFilterVehicleVersionId] = useState<string>("__all__");
  const [vehicleModelsFilterList, setVehicleModelsFilterList] = useState<Array<{ id: number; name: string }>>([]);
  const [vehicleVersionsFilterList, setVehicleVersionsFilterList] = useState<Array<{ id: number; name: string }>>([]);
  const [attributeFilters, setAttributeFilters] = useState<Record<string, unknown>>({});
  const [minBase, setMinBase] = useState<string>("");
  const [maxBase, setMaxBase] = useState<string>("");
  const [minLanded, setMinLanded] = useState<string>("");
  const [maxLanded, setMaxLanded] = useState<string>("");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = useState<"price_desc" | "price_asc" | "latest" | "oldest" | "name_asc" | "name_desc">(
    "oldest",
  );
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => window.clearTimeout(id);
  }, [query]);

  useEffect(() => {
    listCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    if (isForbidden) return;
    let mounted = true;
    void (async () => {
      try {
        const rows = await fetchProductAdListings();
        if (mounted) setProductAdListings(rows);
      } catch (e) {
        console.error(e);
        if (mounted) setProductAdListings([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [version, isForbidden]);

  useEffect(() => {
    if (!canViewPrices && (sortBy === "price_desc" || sortBy === "price_asc")) {
      setSortBy("name_asc");
    }
  }, [canViewPrices, sortBy]);

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories) map.set(c.id, c.name);
    return map;
  }, [categories]);

  const brandNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of listBrandRecords()) map.set(b.id, b.name);
    return map;
  }, []);

  const brands = useMemo(() => listBrandRecords(), []);

  const adListingsByProductId = useMemo(() => {
    const m = new Map<string, ProductAdListingRow[]>();
    for (const row of productAdListings) {
      const id = row.productId;
      if (!id) continue;
      const arr = m.get(id) ?? [];
      arr.push(row);
      m.set(id, arr);
    }
    for (const [, arr] of m) {
      arr.sort((a, b) => b.id - a.id);
    }
    return m;
  }, [productAdListings]);

  const startAdPost = useCallback(
    (p: ProductRecord, platform: ProductPostingPlatform) => {
      const listings = adListingsByProductId.get(p.id) ?? [];
      setAdPost(
        buildMarketplaceAdPostPayload(p, platform, {
          canViewPrices,
          categoryNameById,
          brandNameById,
          listingsForProduct: listings,
        }),
      );
    },
    [adListingsByProductId, canViewPrices, categoryNameById, brandNameById],
  );

  const filterableAttrsForSelectedCategory = useMemo(() => {
    const collect = (categoryId: string): Attribute[] => {
      const schema = buildCategoryProductSchema(categoryId);
      return (
        schema?.groups
          .flatMap((g) => g.attributes)
          .filter((a) => Boolean(a.isFilter)) ?? []
      );
    };

    if (filterCategoryId !== "__all__") return collect(filterCategoryId);

    // Union across all known categories when "All categories" is selected.
    const byCode = new Map<string, Attribute>();
    for (const c of categories) {
      for (const a of collect(c.id)) {
        if (!byCode.has(a.code)) byCode.set(a.code, a);
      }
    }
    return [...byCode.values()];
  }, [filterCategoryId, categories]);

  useEffect(() => {
    // prune stale attribute filter keys when available attributes change
    const valid = new Set(filterableAttrsForSelectedCategory.map((a) => a.code));
    setAttributeFilters((prev) => {
      const next: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(prev)) {
        if (valid.has(k)) next[k] = v;
      }
      if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
      return next;
    });
  }, [filterableAttrsForSelectedCategory]);

  const statusFilterRestricts = useMemo(
    () => INVENTORY_STATUS_FILTER_OPTS.some((o) => !statusInclusions[o.value]),
    [statusInclusions],
  );

  useEffect(() => {
    if (filterVehicleYear === "__all__" || filterVehicleBrandId === "__all__" || filterVehicleBrandId === "__none__") {
      setVehicleModelsFilterList([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get(`/api/v1/catalog/brands/${filterVehicleBrandId}/vehicle-models`);
        const list = extractApiDataArray<{ id: number; name: string }>(res.data);
        if (!cancelled) setVehicleModelsFilterList(list.map((r) => ({ id: r.id, name: r.name })));
      } catch {
        if (!cancelled) setVehicleModelsFilterList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterVehicleYear, filterVehicleBrandId]);

  useEffect(() => {
    if (filterVehicleModelId === "__all__") {
      setVehicleVersionsFilterList([]);
      return;
    }
    const mid = Number(filterVehicleModelId);
    if (!Number.isFinite(mid)) {
      setVehicleVersionsFilterList([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.get(`/api/v1/catalog/vehicle-models/${mid}/vehicle-versions`);
        const list = extractApiDataArray<{ id: number; name: string }>(res.data);
        if (!cancelled) setVehicleVersionsFilterList(list.map((r) => ({ id: r.id, name: r.name })));
      } catch {
        if (!cancelled) setVehicleVersionsFilterList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterVehicleModelId]);

  const appliedFiltersCount =
    (filterCategoryId !== "__all__" ? 1 : 0) +
    (filterBrandId !== "__all__" ? 1 : 0) +
    (statusFilterRestricts ? 1 : 0) +
    (filterVehicleYear !== "__all__" ? 1 : 0) +
    (filterVehicleYear !== "__all__" && filterVehicleBrandId !== "__all__" ? 1 : 0) +
    (filterVehicleYear !== "__all__" && filterVehicleModelId !== "__all__" ? 1 : 0) +
    (filterVehicleYear !== "__all__" && filterVehicleVersionId !== "__all__" ? 1 : 0) +
    (filterCreatedDate ? 1 : 0) +
    (quickDate !== "all" ? 1 : 0) +
    (Object.values(attributeFilters).some((v) => v != null && v !== "" && (!(Array.isArray(v)) || v.length > 0)) ? 1 : 0) +
    (canViewPrices
      ? (minBase.trim() ? 1 : 0) + (maxBase.trim() ? 1 : 0) + (minLanded.trim() ? 1 : 0) + (maxLanded.trim() ? 1 : 0)
      : 0);

  const catalogFiltersResetKey = useMemo(
    () =>
      [
        canViewPrices ? "1" : "0",
        debouncedQuery,
        sortBy,
        quickDate,
        filterCategoryId,
        filterBrandId,
        filterCreatedDate ? format(filterCreatedDate, "yyyy-MM-dd") : "",
        minBase,
        maxBase,
        minLanded,
        maxLanded,
        JSON.stringify(attributeFilters),
        INVENTORY_STATUS_FILTER_OPTS.map((o) => (statusInclusions[o.value] ? "1" : "0")).join(""),
        filterVehicleYear,
        filterVehicleBrandId,
        filterVehicleModelId,
        filterVehicleVersionId,
      ].join("|"),
    [
      canViewPrices,
      debouncedQuery,
      sortBy,
      quickDate,
      filterCategoryId,
      filterBrandId,
      filterCreatedDate,
      minBase,
      maxBase,
      minLanded,
      maxLanded,
      attributeFilters,
      statusInclusions,
      filterVehicleYear,
      filterVehicleBrandId,
      filterVehicleModelId,
      filterVehicleVersionId,
    ],
  );

  const buildCatalogListParams = useCallback((): Omit<FetchProductsCatalogParams, "cursor"> => {
    const effectiveSort =
      !canViewPrices && (sortBy === "price_desc" || sortBy === "price_asc") ? "name_asc" : sortBy;
    const sortApi: string =
      effectiveSort === "name_asc"
        ? "name:asc"
        : effectiveSort === "name_desc"
          ? "name:desc"
          : effectiveSort;

    const params: Omit<FetchProductsCatalogParams, "cursor"> = {
      search: debouncedQuery || undefined,
      sort: sortApi,
    };

    if (filterCategoryId !== "__all__") {
      params.category_id = filterCategoryId;
    }
    if (filterBrandId === "__none__") {
      params.brand_none = true;
    } else if (filterBrandId !== "__all__") {
      params.brand_id = filterBrandId;
    }

    if (filterVehicleYear !== "__all__") {
      params.model_year = filterVehicleYear;
      if (filterVehicleBrandId === "__none__") {
        params.vehicle_brand_none = true;
      } else if (filterVehicleBrandId !== "__all__") {
        params.vehicle_brand_id = filterVehicleBrandId;
      }
      if (filterVehicleModelId !== "__all__") {
        params.vehicle_model_id = filterVehicleModelId;
      }
      if (filterVehicleVersionId !== "__all__") {
        params.vehicle_version_id = filterVehicleVersionId;
      }
    }

    if (filterCreatedDate) {
      params.created_on = format(filterCreatedDate, "yyyy-MM-dd");
    }

    const now = new Date();
    const end = endOfDay(now);
    if (quickDate !== "all") {
      const start =
        quickDate === "today"
          ? startOfDay(now)
          : quickDate === "week"
            ? startOfDay(subDays(now, 6))
            : startOfMonth(now);
      params.created_from = start.toISOString();
      params.created_to = end.toISOString();
    }

    if (canViewPrices) {
      if (minBase.trim()) params.min_purchase_price = minBase;
      if (maxBase.trim()) params.max_purchase_price = maxBase;
      if (minLanded.trim()) params.min_landed_cost = minLanded;
      if (maxLanded.trim()) params.max_landed_cost = maxLanded;
    }

    if (statusFilterRestricts) {
      const codes = INVENTORY_STATUS_FILTER_OPTS.filter((o) => statusInclusions[o.value]).map((o) => o.value);
      if (codes.length === 0) {
        params.search = params.search ? `${params.search} ___catalog_empty_status___` : "___catalog_empty_status___";
      } else if (codes.length < INVENTORY_STATUS_FILTER_OPTS.length) {
        params.inventory_status = codes.join(",");
      }
    }

    const cleanedAttrs = Object.fromEntries(
      Object.entries(attributeFilters).filter(([, v]) => {
        if (v == null) return false;
        if (v === "") return false;
        if (Array.isArray(v)) return v.length > 0;
        return true;
      }),
    );
    if (Object.keys(cleanedAttrs).length > 0) {
      params.attribute_filters = JSON.stringify(cleanedAttrs);
    }

    return params;
  }, [
    debouncedQuery,
    sortBy,
    canViewPrices,
    filterCategoryId,
    filterBrandId,
    filterVehicleYear,
    filterVehicleBrandId,
    filterVehicleModelId,
    filterVehicleVersionId,
    filterCreatedDate,
    quickDate,
    minBase,
    maxBase,
    minLanded,
    maxLanded,
    statusFilterRestricts,
    statusInclusions,
    attributeFilters,
  ]);

  const activeFilterChips = useMemo(() => {
    const chips: Array<{ id: string; label: string; onClear: () => void }> = [];
    if (filterCategoryId !== "__all__") {
      const catName = categories.find((c) => c.id === filterCategoryId)?.name ?? filterCategoryId;
      chips.push({ id: "category", label: `Category: ${catName}`, onClear: () => setFilterCategoryId("__all__") });
    }
    if (filterBrandId !== "__all__") {
      const bName = brands.find((b) => b.id === filterBrandId)?.name ?? (filterBrandId === "__none__" ? "No brand" : filterBrandId);
      chips.push({ id: "brand", label: `Brand: ${bName}`, onClear: () => setFilterBrandId("__all__") });
    }
    if (filterVehicleYear !== "__all__") {
      chips.push({ id: "year", label: `Year: ${filterVehicleYear}`, onClear: () => setFilterVehicleYear("__all__") });
    }
    if (filterVehicleBrandId !== "__all__") {
      const bName = brands.find((b) => b.id === filterVehicleBrandId)?.name ?? filterVehicleBrandId;
      chips.push({ id: "make", label: `Make: ${bName}`, onClear: () => setFilterVehicleBrandId("__all__") });
    }
    if (filterVehicleModelId !== "__all__") {
      const mName = vehicleModelsFilterList.find((m) => String(m.id) === filterVehicleModelId)?.name ?? filterVehicleModelId;
      chips.push({ id: "model", label: `Model: ${mName}`, onClear: () => setFilterVehicleModelId("__all__") });
    }
    if (filterVehicleVersionId !== "__all__") {
      const vName = vehicleVersionsFilterList.find((v) => String(v.id) === filterVehicleVersionId)?.name ?? filterVehicleVersionId;
      chips.push({ id: "version", label: `Version: ${vName}`, onClear: () => setFilterVehicleVersionId("__all__") });
    }
    const uncheckedStatuses = INVENTORY_STATUS_FILTER_OPTS.filter((o) => !statusInclusions[o.value]);
    if (uncheckedStatuses.length > 0 && uncheckedStatuses.length < INVENTORY_STATUS_FILTER_OPTS.length) {
      chips.push({
        id: "status",
        label: `Status: ${INVENTORY_STATUS_FILTER_OPTS.filter((o) => statusInclusions[o.value]).map((o) => o.label).join(", ")}`,
        onClear: () => setStatusInclusions(Object.fromEntries(INVENTORY_STATUS_FILTER_OPTS.map((o) => [o.value, true]))),
      });
    }
    if (minBase || maxBase) {
      chips.push({
        id: "baseCost",
        label: `Cost: ${minBase || "0"} - ${maxBase || "∞"}`,
        onClear: () => { setMinBase(""); setMaxBase(""); },
      });
    }
    if (minLanded || maxLanded) {
      chips.push({
        id: "landedCost",
        label: `Landed: ${minLanded || "0"} - ${maxLanded || "∞"}`,
        onClear: () => { setMinLanded(""); setMaxLanded(""); },
      });
    }
    return chips;
  }, [
    filterCategoryId,
    filterBrandId,
    filterVehicleYear,
    filterVehicleBrandId,
    filterVehicleModelId,
    filterVehicleVersionId,
    categories,
    brands,
    vehicleModelsFilterList,
    vehicleVersionsFilterList,
    statusInclusions,
    minBase,
    maxBase,
    minLanded,
    maxLanded,
  ]);

  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogPageSize, setCatalogPageSize] = useState<number>(PRODUCTS_PAGE_SIZE_OPTIONS[0]);
  const [catalogRows, setCatalogRows] = useState<ProductRecord[]>([]);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogLastPage, setCatalogLastPage] = useState(1);
  const [catalogLoading, setCatalogLoading] = useState(true);
  /** First catalog API response — same UX as Vehicle Versions (glass-card + spinner). */
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [catalogNonce, setCatalogNonce] = useState(0);
  /** Bumps when attribute / category-link catalogs finish syncing so listing attrs recompute. */
  const [listingAttrTick, setListingAttrTick] = useState(0);
  /** Avoid duplicate catalog fetches when filters change before page resets to 1. */
  const catalogListFilterKeyRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await syncCatalogAttributesFromApi();
        if (!cancelled) setListingAttrTick((t) => t + 1);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const categoryIds = [
      ...new Set(catalogRows.map((p) => p.categoryId).filter((id) => Boolean(id))),
    ];
    if (categoryIds.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        await Promise.all(categoryIds.map((id) => syncLinksForCategoryFromApi(id)));
        if (!cancelled) setListingAttrTick((t) => t + 1);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogRows]);

  useEffect(() => {
    let cancelled = false;

    const prevKey = catalogListFilterKeyRef.current;
    const filtersChanged = prevKey !== null && prevKey !== catalogFiltersResetKey;
    if (filtersChanged && catalogPage !== 1) {
      catalogListFilterKeyRef.current = catalogFiltersResetKey;
      setCatalogPage(1);
      return () => {
        cancelled = true;
      };
    }

    catalogListFilterKeyRef.current = catalogFiltersResetKey;

    void (async () => {
      setCatalogLoading(true);
      try {
        const { data, meta } = await fetchProductsCatalogPage({
          ...buildCatalogListParams(),
          page: catalogPage,
          per_page: catalogPageSize,
        });
        if (cancelled) return;
        // Commit rows + meta + loading flags in one sync flush so the browser paints
        // products as soon as the API returns (avoids waiting on deferred work in the same tick).
        flushSync(() => {
          setCatalogRows(data);
          setCatalogTotal(meta.total);
          setCatalogLastPage(meta.last_page);
          setCatalogPage((prev) => (meta.current_page !== prev ? meta.current_page : prev));
          setCatalogPageSize((prev) => (meta.per_page !== prev ? meta.per_page : prev));
          setCatalogLoading(false);
          setIsBootstrapping(false);
        });
      } catch (e) {
        if (cancelled) return;
        if (isAxiosError(e) && e.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error(e);
        toast.error(catalogProductErrorMessage(e));
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
          setIsBootstrapping(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally omit buildCatalogListParams: its inputs are covered by catalogFiltersResetKey
    // so the callback identity does not trigger redundant requests (e.g. after attribute prune).
  }, [catalogFiltersResetKey, catalogPage, catalogPageSize, catalogNonce]);

  const refetchCatalog = useCallback(() => {
    setCatalogNonce((n) => n + 1);
  }, []);

  const catalogTotalPages = Math.max(1, catalogLastPage);
  const catalogRangeStart =
    catalogTotal === 0 ? 0 : (Math.min(catalogPage, catalogTotalPages) - 1) * catalogPageSize + 1;
  const catalogRangeEnd =
    catalogTotal === 0
      ? 0
      : Math.min(Math.min(catalogPage, catalogTotalPages) * catalogPageSize, catalogTotal);

  /** Defer heavy schema work so the first paint after fetch can show cards sooner. */
  const deferredCatalogRows = useDeferredValue(catalogRows);
  const listingAttrsByCategory = useMemo(() => {
    const map = new Map<string, Attribute[]>();
    for (const p of deferredCatalogRows) {
      if (map.has(p.categoryId)) continue;
      const schema = buildCategoryProductSchema(p.categoryId);
      const attrs =
        schema?.groups
          .flatMap((g) => g.attributes)
          .filter((a) => Boolean(a.isListing)) ?? [];
      map.set(p.categoryId, attrs);
    }
    return map;
  }, [deferredCatalogRows, listingAttrTick]);

  const resolveCatalogProduct = useCallback(
    (id: string | null | undefined) => {
      if (id == null) return undefined;
      return catalogRows.find((x) => String(x.id) === String(id)) ?? getProductById(id);
    },
    [catalogRows],
  );

  const handlePublishToInStock = useCallback(
    async (p: ProductRecord) => {
      if (publishBusyId != null) return;
      setPublishBusyId(p.id);
      try {
        await publishProductToInStock(p.id);
        refetchCatalog();
        refresh();
        toast.success("Product published — now in stock.");
      } catch (e) {
        toast.error(catalogProductErrorMessage(e));
      } finally {
        setPublishBusyId(null);
      }
    },
    [publishBusyId, refresh, refetchCatalog]
  );

  const handleCopyAsDraft = useCallback(
    async (p: ProductRecord) => {
      if (copyBusyId != null) return;
      const sections = (
        [
          copySections.basic ? "basic" : null,
          copySections.additional ? "additional" : null,
          copySections.media ? "media" : null,
          canViewPrices && copySections.purchase ? "purchase" : null,
        ] as const
      ).filter((s): s is "basic" | "additional" | "media" | "purchase" => s != null);

      if (sections.length === 0) {
        toast.error("Select at least one section to copy.");
        return;
      }

      setCopyBusyId(p.id);
      try {
        const copy = await copyProductAsDraft(p.id, sections);
        setCopyConfirmProduct(null);
        refetchCatalog();
        refresh();
        toast.success("Product copied as draft.");
        navigate(`/products/${copy.id}/edit`);
      } catch (e) {
        toast.error(catalogProductErrorMessage(e));
      } finally {
        setCopyBusyId(null);
      }
    },
    [canViewPrices, copyBusyId, copySections, navigate, refresh, refetchCatalog],
  );

  return isForbidden ? (
    <PermissionDenied
      title="Products access required"
      message="You don’t have permission to view the product catalog. Ask an administrator to grant product_can_view_any."
    />
  ) : (
    <Layout title="Products">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/10 shadow-sm text-primary">
              <Car className="h-5.5 w-5.5" />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <h1 className="text-2xl font-display font-bold text-foreground sm:text-3xl">
                  Vehicle Catalog
                </h1>
                <span className="inline-flex items-center rounded-full border border-primary/25 bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
                  {catalogTotal} {catalogTotal === 1 ? "Item" : "Items"}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Manage, filter, publish, and list your vehicle inventory
              </p>
            </div>
          </div>
          {canCreate ? (
            <Link to="/products/add" className="shrink-0">
              <Button variant="premium" size="default" className="gap-2 shadow-sm font-semibold text-xs h-10 px-4">
                <Plus className="w-4 h-4" />
                <span>Add Product</span>
              </Button>
            </Link>
          ) : null}
        </div>

        {/* Filters + sort/date/view (top bar) */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, slug, category, brand..."
              className="glass-input pl-9 h-10"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Filter products</SheetTitle>
                <SheetDescription>
                  Refine the list by category, brand, inventory status, vehicle year/make/model/version, created date,
                  and cost ranges.
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Category</Label>
                    <Select value={filterCategoryId} onValueChange={setFilterCategoryId}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="All categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All categories</SelectItem>
                        {categories.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Brand</Label>
                    <Select value={filterBrandId} onValueChange={setFilterBrandId}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="All brands" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All brands</SelectItem>
                        <SelectItem value="__none__">No brand</SelectItem>
                        {brands.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-3">
                  <p className="text-sm font-medium text-foreground">Inventory status</p>
                  <p className="text-xs text-muted-foreground">Uncheck a status to hide matching products.</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {INVENTORY_STATUS_FILTER_OPTS.map((o) => (
                      <label key={o.value} className="flex cursor-pointer items-center gap-2 text-sm">
                        <Checkbox
                          checked={Boolean(statusInclusions[o.value])}
                          onCheckedChange={(c) =>
                            setStatusInclusions((prev) => ({ ...prev, [o.value]: c === true }))
                          }
                        />
                        <span>{o.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                  <p className="text-sm font-medium text-foreground">Vehicle</p>
                  <p className="text-xs text-muted-foreground">
                    Select a model year first. Make, then model, then version narrow the list (optional steps after
                    year).
                  </p>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Model year</Label>
                    <Select
                      value={filterVehicleYear}
                      onValueChange={(v) => {
                        setFilterVehicleYear(v);
                        if (v === "__all__") {
                          setFilterVehicleBrandId("__all__");
                          setFilterVehicleModelId("__all__");
                          setFilterVehicleVersionId("__all__");
                          setVehicleModelsFilterList([]);
                          setVehicleVersionsFilterList([]);
                        }
                      }}
                    >
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue placeholder="Any year" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[min(60vh,20rem)]">
                        <SelectItem value="__all__">Any year</SelectItem>
                        {MODEL_YEARS_DESC.map((y) => (
                          <SelectItem key={y} value={String(y)}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Make (brand)</Label>
                      <Select
                        value={filterVehicleBrandId}
                        disabled={filterVehicleYear === "__all__"}
                        onValueChange={(v) => {
                          setFilterVehicleBrandId(v);
                          setFilterVehicleModelId("__all__");
                          setFilterVehicleVersionId("__all__");
                          setVehicleVersionsFilterList([]);
                        }}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="All makes" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All makes</SelectItem>
                          <SelectItem value="__none__">No make</SelectItem>
                          {brands.map((b) => (
                            <SelectItem key={b.id} value={b.id}>
                              {b.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {filterVehicleYear === "__all__" ? (
                        <p className="text-[11px] text-muted-foreground">Choose a model year to enable make.</p>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Model</Label>
                      <Select
                        value={filterVehicleModelId}
                        disabled={
                          filterVehicleYear === "__all__" ||
                          filterVehicleBrandId === "__all__" ||
                          filterVehicleBrandId === "__none__"
                        }
                        onValueChange={(v) => {
                          setFilterVehicleModelId(v);
                          setFilterVehicleVersionId("__all__");
                        }}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="All models" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[min(50vh,16rem)]">
                          <SelectItem value="__all__">All models</SelectItem>
                          {vehicleModelsFilterList.map((m) => (
                            <SelectItem key={m.id} value={String(m.id)}>
                              {m.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label className="text-xs text-muted-foreground">Version</Label>
                      <Select
                        value={filterVehicleVersionId}
                        disabled={filterVehicleModelId === "__all__"}
                        onValueChange={setFilterVehicleVersionId}
                      >
                        <SelectTrigger className="glass-input h-10">
                          <SelectValue placeholder="Any version" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[min(50vh,16rem)]">
                          <SelectItem value="__all__">Any version</SelectItem>
                          {vehicleVersionsFilterList.map((v) => (
                            <SelectItem key={v.id} value={String(v.id)}>
                              {v.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Created date</Label>
                  <Popover modal={true}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={cn(
                          "glass-input h-10 w-full justify-start text-left font-normal",
                          !filterCreatedDate && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                        {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                      <Calendar
                        mode="single"
                        selected={filterCreatedDate}
                        onSelect={setFilterCreatedDate}
                        initialFocus
                      />
                      <div className="flex gap-2 border-t p-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="flex-1"
                          onClick={() => setFilterCreatedDate(undefined)}
                        >
                          Clear
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                  <p className="text-sm font-medium text-foreground">Attribute filters</p>
                  {filterableAttrsForSelectedCategory.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {filterCategoryId === "__all__"
                        ? "No attributes are marked “Show in filters” yet."
                        : "No attributes are marked “Show in filters” for this category."}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {filterableAttrsForSelectedCategory.map((a) => {
                        const current = attributeFilters[a.code];

                        if (a.type === "select") {
                          return (
                            <div key={a.id} className="space-y-2">
                              <Label className="text-xs text-muted-foreground">{a.name}</Label>
                              <Select
                                value={typeof current === "string" ? current : "__any__"}
                                onValueChange={(v) =>
                                  setAttributeFilters((s) => ({ ...s, [a.code]: v }))
                                }
                              >
                                <SelectTrigger className="glass-input h-10">
                                  <SelectValue placeholder="Any" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__any__">Any</SelectItem>
                                  {(a.options ?? []).map((o) => (
                                    <SelectItem key={o.id} value={o.value}>
                                      {o.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        }

                        if (a.type === "multiselect") {
                          const selected = Array.isArray(current) ? (current as string[]) : [];
                          return (
                            <div key={a.id} className="space-y-2 sm:col-span-2">
                              <Label className="text-xs text-muted-foreground">{a.name}</Label>
                              <div className="flex flex-wrap gap-2">
                                {(a.options ?? []).map((o) => {
                                  const checked = selected.includes(o.value);
                                  return (
                                    <button
                                      key={o.id}
                                      type="button"
                                      className={[
                                        "rounded-full border px-3 py-1 text-xs transition-colors",
                                        checked
                                          ? "border-primary/40 bg-primary/10 text-foreground"
                                          : "border-border/50 bg-background/50 text-muted-foreground hover:bg-muted/20",
                                      ].join(" ")}
                                      onClick={() => {
                                        const next = checked
                                          ? selected.filter((v) => v !== o.value)
                                          : [...selected, o.value];
                                        setAttributeFilters((s) => ({ ...s, [a.code]: next }));
                                      }}
                                    >
                                      {o.label}
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-[11px] text-muted-foreground">
                                  {selected.length ? `${selected.length} selected` : "Any"}
                                </span>
                                {selected.length ? (
                                  <button
                                    type="button"
                                    className="text-[11px] text-primary hover:underline"
                                    onClick={() => setAttributeFilters((s) => ({ ...s, [a.code]: [] }))}
                                  >
                                    Clear
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          );
                        }

                        if (a.type === "boolean") {
                          const v = current;
                          return (
                            <div key={a.id} className="space-y-2">
                              <Label className="text-xs text-muted-foreground">{a.name}</Label>
                              <Select
                                value={v === true ? "true" : v === false ? "false" : "__any__"}
                                onValueChange={(next) =>
                                  setAttributeFilters((s) => ({
                                    ...s,
                                    [a.code]: next === "__any__" ? "__any__" : next === "true",
                                  }))
                                }
                              >
                                <SelectTrigger className="glass-input h-10">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__any__">Any</SelectItem>
                                  <SelectItem value="true">Yes</SelectItem>
                                  <SelectItem value="false">No</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        }

                        if (a.type === "number") {
                          return (
                            <div key={a.id} className="space-y-2">
                              <Label className="text-xs text-muted-foreground">{a.name}</Label>
                              <Input
                                className="glass-input h-10"
                                type="number"
                                value={typeof current === "string" ? current : ""}
                                onChange={(e) =>
                                  setAttributeFilters((s) => ({ ...s, [a.code]: e.target.value }))
                                }
                                placeholder="Type to match…"
                              />
                            </div>
                          );
                        }

                        if (a.type === "date") {
                          return (
                            <div key={a.id} className="space-y-2">
                              <Label className="text-xs text-muted-foreground">{a.name}</Label>
                              <Input
                                className="glass-input h-10"
                                type="date"
                                value={typeof current === "string" ? current : ""}
                                onChange={(e) =>
                                  setAttributeFilters((s) => ({ ...s, [a.code]: e.target.value }))
                                }
                              />
                            </div>
                          );
                        }

                        // text/textarea fallback
                        return (
                          <div key={a.id} className="space-y-2">
                            <Label className="text-xs text-muted-foreground">{a.name}</Label>
                            <Input
                              className="glass-input h-10"
                              value={typeof current === "string" ? current : ""}
                              onChange={(e) =>
                                setAttributeFilters((s) => ({ ...s, [a.code]: e.target.value }))
                              }
                              placeholder="Type to match…"
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {canViewPrices ? (
                  <>
                    <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                      <p className="text-sm font-medium text-foreground">Base purchase cost range</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Min</Label>
                          <Input
                            className="glass-input h-10"
                            type="number"
                            min={0}
                            step="any"
                            value={minBase}
                            onChange={(e) => setMinBase(e.target.value)}
                            placeholder="0"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Max</Label>
                          <Input
                            className="glass-input h-10"
                            type="number"
                            min={0}
                            step="any"
                            value={maxBase}
                            onChange={(e) => setMaxBase(e.target.value)}
                            placeholder="—"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                      <p className="text-sm font-medium text-foreground">Landed cost range</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Min</Label>
                          <Input
                            className="glass-input h-10"
                            type="number"
                            min={0}
                            step="any"
                            value={minLanded}
                            onChange={(e) => setMinLanded(e.target.value)}
                            placeholder="0"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs text-muted-foreground">Max</Label>
                          <Input
                            className="glass-input h-10"
                            type="number"
                            min={0}
                            step="any"
                            value={maxLanded}
                            onChange={(e) => setMaxLanded(e.target.value)}
                            placeholder="—"
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </div>

              <SheetFooter className="mt-6 gap-2">
                <Button
                  type="button"
                  variant="glass"
                  className="w-full"
                  onClick={() => {
                    setFilterCategoryId("__all__");
                    setFilterBrandId("__all__");
                    setStatusInclusions(Object.fromEntries(INVENTORY_STATUS_FILTER_OPTS.map((o) => [o.value, true])));
                    setFilterVehicleYear("__all__");
                    setFilterVehicleBrandId("__all__");
                    setFilterVehicleModelId("__all__");
                    setFilterVehicleVersionId("__all__");
                    setVehicleModelsFilterList([]);
                    setVehicleVersionsFilterList([]);
                    setAttributeFilters({});
                    setMinBase("");
                    setMaxBase("");
                    setMinLanded("");
                    setMaxLanded("");
                    setFilterCreatedDate(undefined);
                  }}
                >
                  Reset
                </Button>
                <SheetClose asChild>
                  <Button type="button" variant="premium" className="w-full">
                    Apply filters
                  </Button>
                </SheetClose>
              </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          {activeFilterChips.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mr-1">Active Filters:</span>
              {activeFilterChips.map((chip) => (
                <span
                  key={chip.id}
                  className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary"
                >
                  <span>{chip.label}</span>
                  <button
                    type="button"
                    onClick={chip.onClear}
                    className="rounded-full p-0.5 hover:bg-primary/20 transition-colors"
                    aria-label={`Remove filter ${chip.label}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              <button
                type="button"
                onClick={() => {
                  setFilterCategoryId("__all__");
                  setFilterBrandId("__all__");
                  setStatusInclusions(Object.fromEntries(INVENTORY_STATUS_FILTER_OPTS.map((o) => [o.value, true])));
                  setFilterVehicleYear("__all__");
                  setFilterVehicleBrandId("__all__");
                  setFilterVehicleModelId("__all__");
                  setFilterVehicleVersionId("__all__");
                  setMinBase("");
                  setMaxBase("");
                  setMinLanded("");
                  setMaxLanded("");
                }}
                className="text-xs font-semibold text-muted-foreground hover:text-foreground underline ml-2"
              >
                Clear All
              </button>
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {([
                  { id: "today", label: "Today", mobile: "Today" },
                  { id: "week", label: "Week", mobile: "7D" },
                  { id: "month", label: "Month", mobile: "30D" },
                  { id: "all", label: "All", mobile: "All" },
                ] as const).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid"
                    ? "bg-primary/10 text-foreground"
                    : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list"
                    ? "bg-primary/10 text-foreground"
                    : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="products-page-size"
              pageSize={catalogPageSize}
              onPageSizeChange={(n) => {
                setCatalogPageSize(n);
                setCatalogPage(1);
              }}
              options={PRODUCTS_PAGE_SIZE_OPTIONS}
            />
            <div className="w-[170px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="oldest">ID: Low to High</SelectItem>
                  <SelectItem value="latest">ID: High to Low</SelectItem>
                  {canViewPrices ? (
                    <>
                      <SelectItem value="price_desc">Price: High to Low</SelectItem>
                      <SelectItem value="price_asc">Price: Low to High</SelectItem>
                    </>
                  ) : null}
                  <SelectItem value="name_asc">Name: A to Z</SelectItem>
                  <SelectItem value="name_desc">Name: Z to A</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="products-page-size-desktop"
              pageSize={catalogPageSize}
              onPageSizeChange={(n) => {
                setCatalogPageSize(n);
                setCatalogPage(1);
              }}
              options={PRODUCTS_PAGE_SIZE_OPTIONS}
            />

            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[190px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oldest">ID: Low to High</SelectItem>
                    <SelectItem value="latest">ID: High to Low</SelectItem>
                    {canViewPrices ? (
                      <>
                        <SelectItem value="price_desc">Price: High to Low</SelectItem>
                        <SelectItem value="price_asc">Price: Low to High</SelectItem>
                      </>
                    ) : null}
                    <SelectItem value="name_asc">Name: A to Z</SelectItem>
                    <SelectItem value="name_desc">Name: Z to A</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {([
                  { id: "today", label: "Today" },
                  { id: "week", label: "Week" },
                  { id: "month", label: "Month" },
                  { id: "all", label: "All" },
                ] as const).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid"
                      ? "bg-primary/10 text-foreground"
                      : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list"
                      ? "bg-primary/10 text-foreground"
                      : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid — server page/per_page with total count (classic pagination footer) */}
        {isBootstrapping && catalogRows.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
            Loading products…
          </div>
        ) : null}
        {!isBootstrapping && !catalogLoading && catalogRows.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            No products match your filters or search. Try adjusting filters or clearing the search box.
          </div>
        ) : null}
        {catalogRows.length > 0 ? (
        <div className="relative space-y-4">
        {viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {catalogRows.map((p, i) => {
              const statusPill = inventoryCardStatusPill(p.inventoryStatus);
              const images = (p.productImages ?? []).filter((u) => Boolean(u.url));
              const hero = images[0]?.url ?? null;
              const imageCount = images.length;
              return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.012, 0.08) }}
                className="glass-card group relative flex h-full flex-col overflow-hidden border border-border/40 bg-card/70 shadow-sm transition-shadow hover:shadow-md"
              >
                <span
                  className={cn(
                    "absolute right-3 top-3 z-20 inline-flex items-center gap-1.5 select-none rounded-full px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em]",
                    statusPill.className,
                  )}
                  aria-label={`Inventory: ${statusPill.label}`}
                >
                  <span className={cn("h-1.5 w-1.5 rounded-full", statusPill.dotClassName)} aria-hidden />
                  {statusPill.label}
                </span>
                <div className="relative h-44 overflow-hidden bg-gradient-to-br from-secondary/60 via-muted/40 to-muted">
                  {hero ? (
                    <button
                      type="button"
                      className="absolute inset-0 block h-full w-full outline-none"
                      onClick={() =>
                        setLightbox({
                          open: true,
                          productId: String(p.id),
                          index: 0,
                          zoom: 1,
                        })
                      }
                      aria-label="Open product images"
                    >
                      <img
                        src={hero}
                        alt={images[0]?.filename || p.name}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                        loading="lazy"
                      />
                      <span className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" aria-hidden />
                    </button>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Car className="h-16 w-16 text-muted-foreground/30" />
                    </div>
                  )}

                  {imageCount > 1 ? (
                    <span className="absolute bottom-3 left-3 z-20 inline-flex items-center gap-1 rounded-full border border-white/15 bg-black/35 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
                      {imageCount} photos
                    </span>
                  ) : null}
                </div>

                <div className="flex flex-1 flex-col gap-3 p-4">
                  <div className="space-y-3">
                    <div>
                    <div className="flex min-w-0 items-start gap-1.5">
                      <h3 className="min-w-0 flex-1 font-display font-semibold leading-snug text-foreground text-sm">
                        {p.name}
                      </h3>
                      {(p.inventoryStatus ?? "") === "in_stock" ? (
                        <ProductHeadingMarketplaceIcons
                          listings={adListingsByProductId.get(p.id) ?? []}
                          canViewPrices={canViewPrices}
                        />
                      ) : null}
                    </div>
                    {canViewPrices ? (
                      <ProductCardPrices product={p} />
                    ) : (
                      <p className="text-sm text-muted-foreground mt-0.5" title="Pricing is restricted">
                        —
                      </p>
                    )}
                    </div>

                    {(() => {
                      const attrs = listingAttrsByCategory.get(p.categoryId) ?? [];
                      const picked = attrs.slice(0, 6);
                      if (picked.length === 0) return null;
                      return (
                        <ProductListingAttrPipes attributes={picked} values={p.attributes} />
                      );
                    })()}
                  </div>
                  <div className="flex items-center flex-wrap gap-1.5 pt-2.5 border-t border-border/40 mt-auto min-w-0">
                    {canViewDetail ? (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Link to={`/products/${p.id}`} className="shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors"
                              aria-label="View Details"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="top">View Details</TooltipContent>
                      </Tooltip>
                    ) : null}

                    {canUpdate ? (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Link to={`/products/${p.id}/edit`} className="shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-8 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors"
                              aria-label="Edit Product"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="top">Edit Vehicle</TooltipContent>
                      </Tooltip>
                    ) : null}

                    {canCreate ? (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors"
                            onClick={() => {
                              setCopySections({ basic: true, additional: true, media: true, purchase: true });
                              setCopyConfirmProduct(p);
                            }}
                            disabled={copyBusyId != null}
                            aria-label="Copy this product"
                          >
                            {copyBusyId === p.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                            ) : (
                              <Copy className="h-3.5 w-3.5" aria-hidden />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Copy this product</TooltipContent>
                      </Tooltip>
                    ) : null}

                    {canUpdate && (p.inventoryStatus ?? "") === "draft" ? (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="default"
                            size="sm"
                            className="h-8 gap-1.5 px-2.5 bg-amber-500 hover:bg-amber-600 text-amber-950 font-bold text-xs shadow-xs"
                            disabled={publishBusyId === p.id}
                            onClick={() => setPublishConfirmProduct(p)}
                          >
                            {publishBusyId === p.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                            ) : (
                              <Send className="h-3.5 w-3.5" aria-hidden />
                            )}
                            <span>Publish</span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Publish to In-Stock</TooltipContent>
                      </Tooltip>
                    ) : null}

                    {canCreateSale && (p.inventoryStatus ?? "") === "in_stock" ? (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Link to={`/product-sales/new?product_id=${encodeURIComponent(p.id)}`} className="shrink-0">
                            <Button
                              variant="default"
                              size="sm"
                              className="h-8 w-8 p-0 bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
                              aria-label="Record sale"
                            >
                              <Receipt className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent side="top">Record sale</TooltipContent>
                      </Tooltip>
                    ) : null}

                    {canUpdate && (p.inventoryStatus ?? "") === "in_stock" ? (
                      <DropdownMenu>
                        <Tooltip delayDuration={200}>
                          <TooltipTrigger asChild>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1.5 px-2.5 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors text-xs font-semibold"
                                aria-label="Ad Post to marketplace"
                              >
                                <Megaphone className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                          </TooltipTrigger>
                          <TooltipContent side="top">Marketplace Ad Post</TooltipContent>
                        </Tooltip>
                        <DropdownMenuContent align="end">
                          <ProductMarketplaceMenuItems product={p} onPost={startAdPost} iconSizeClassName="h-4 w-4" />
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : null}
                    {canUpdate ? (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors shrink-0"
                            onClick={() => {
                              setStatusModalProduct(p);
                              setNextInventoryStatus("");
                              setStatusReason("");
                            }}
                            aria-label="Change inventory status"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Change Inventory Status</TooltipContent>
                      </Tooltip>
                    ) : null}

                    {canDelete ? (
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-8 w-8 shrink-0 border-destructive/40 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive/60 transition-colors"
                            onClick={() => setDeleteTargetId(p.id)}
                            aria-label="Delete product"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">Delete Product</TooltipContent>
                      </Tooltip>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            );
            })}
          </div>
        ) : (
          <div className="space-y-3">
            {catalogRows.map((p, i) => {
              const attrs = listingAttrsByCategory.get(p.categoryId) ?? [];
              const picked = attrs.slice(0, 6);
              const statusPill = inventoryCardStatusPill(p.inventoryStatus);
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.01, 0.06) }}
                  className="glass-card p-3 sm:p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="h-16 w-20 sm:h-20 sm:w-24 rounded-lg overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                        {p.productImages?.[0]?.url ? (
                          <img
                            src={p.productImages[0].url}
                            alt={p.productImages[0].filename || p.name}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <Car className="w-8 h-8 text-muted-foreground/40" />
                        )}
                      </div>

                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <h3 className="font-display font-bold text-sm text-foreground truncate" title={p.name}>
                                {p.name}
                              </h3>
                              {(p.inventoryStatus ?? "") === "in_stock" ? (
                                <ProductHeadingMarketplaceIcons
                                  listings={adListingsByProductId.get(p.id) ?? []}
                                  canViewPrices={canViewPrices}
                                />
                              ) : null}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                              {(categoryNameById.get(p.categoryId) ?? "—")}
                              {p.brandId ? ` · ${brandNameById.get(p.brandId) ?? "—"}` : ""}
                            </p>
                            {canViewPrices ? (
                              <div className="pt-0.5">
                                <ProductCardPrices product={p} />
                              </div>
                            ) : null}
                          </div>

                          <span
                            className={cn(
                              "shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-[0.12em]",
                              statusPill.className,
                            )}
                          >
                            <span className={cn("h-1.5 w-1.5 rounded-full", statusPill.dotClassName)} aria-hidden />
                            {statusPill.label}
                          </span>
                        </div>

                        {picked.length ? (
                          <ProductListingAttrPipes attributes={picked} values={p.attributes} />
                        ) : null}
                      </div>
                    </div>

                    <div className="flex items-center flex-wrap gap-1.5 justify-end border-t border-border/40 pt-2.5">
                        {canViewDetail ? (
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <Link to={`/products/${p.id}`} className="shrink-0">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 w-8 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors"
                                  aria-label="View Details"
                                >
                                  <Eye className="h-3.5 w-3.5" />
                                </Button>
                              </Link>
                            </TooltipTrigger>
                            <TooltipContent side="top">View Details</TooltipContent>
                          </Tooltip>
                        ) : null}

                        {canUpdate ? (
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <Link to={`/products/${p.id}/edit`} className="shrink-0">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-8 w-8 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors"
                                  aria-label="Edit Product"
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                </Button>
                              </Link>
                            </TooltipTrigger>
                            <TooltipContent side="top">Edit Vehicle</TooltipContent>
                          </Tooltip>
                        ) : null}

                        {canCreate ? (
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors"
                                onClick={() => {
                                  setCopySections({ basic: true, additional: true, media: true, purchase: true });
                                  setCopyConfirmProduct(p);
                                }}
                                disabled={copyBusyId != null}
                                aria-label="Copy this product"
                              >
                                {copyBusyId === p.id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                                ) : (
                                  <Copy className="h-3.5 w-3.5" aria-hidden />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">Copy this product</TooltipContent>
                          </Tooltip>
                        ) : null}

                        {canUpdate && (p.inventoryStatus ?? "") === "draft" ? (
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="default"
                                size="sm"
                                className="h-8 gap-1.5 px-2.5 bg-amber-500 hover:bg-amber-600 text-amber-950 font-bold text-xs shadow-xs"
                                disabled={publishBusyId === p.id}
                                onClick={() => setPublishConfirmProduct(p)}
                              >
                                {publishBusyId === p.id ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                                ) : (
                                  <Send className="h-3.5 w-3.5" aria-hidden />
                                )}
                                <span>Publish</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">Publish to In-Stock</TooltipContent>
                          </Tooltip>
                        ) : null}

                        {canCreateSale && (p.inventoryStatus ?? "") === "in_stock" ? (
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <Link to={`/product-sales/new?product_id=${encodeURIComponent(p.id)}`} className="shrink-0">
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="h-8 w-8 p-0 bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs"
                                  aria-label="Record sale"
                                >
                                  <Receipt className="h-3.5 w-3.5" />
                                </Button>
                              </Link>
                            </TooltipTrigger>
                            <TooltipContent side="top">Record sale</TooltipContent>
                          </Tooltip>
                        ) : null}

                        {canUpdate && (p.inventoryStatus ?? "") === "in_stock" ? (
                          <DropdownMenu>
                            <Tooltip delayDuration={200}>
                              <TooltipTrigger asChild>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-8 gap-1.5 px-2.5 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors text-xs font-semibold"
                                    aria-label="Ad Post to marketplace"
                                  >
                                    <Megaphone className="h-3.5 w-3.5" />
                                  </Button>
                                </DropdownMenuTrigger>
                              </TooltipTrigger>
                              <TooltipContent side="top">Marketplace Ad Post</TooltipContent>
                            </Tooltip>
                            <DropdownMenuContent align="end">
                              <ProductMarketplaceMenuItems product={p} onPost={startAdPost} iconSizeClassName="h-3.5 w-3.5" />
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : null}

                        {canUpdate ? (
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover:text-primary transition-colors shrink-0"
                                onClick={() => {
                                  setStatusModalProduct(p);
                                  setNextInventoryStatus("");
                                  setStatusReason("");
                                }}
                                aria-label="Change inventory status"
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">Change Inventory Status</TooltipContent>
                          </Tooltip>
                        ) : null}

                        {canDelete ? (
                          <Tooltip delayDuration={200}>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 shrink-0 border-destructive/40 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive hover:border-destructive/60 transition-colors"
                                onClick={() => setDeleteTargetId(p.id)}
                                aria-label="Delete product"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">Delete Product</TooltipContent>
                          </Tooltip>
                        ) : null}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
        {catalogLoading && catalogRows.length > 0 ? (
          <div className="pointer-events-none absolute bottom-2 right-2 z-10 flex items-center gap-2 rounded-lg border border-border/60 bg-card/95 px-3 py-1.5 text-xs text-muted-foreground shadow-md backdrop-blur-sm">
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0 text-foreground" aria-hidden />
            Updating…
          </div>
        ) : null}
        </div>
        ) : null}

        {!isBootstrapping && catalogTotal > 0 ? (
          <GridPaginationFooter
            totalCount={catalogTotal}
            rangeStart={catalogRangeStart}
            rangeEnd={catalogRangeEnd}
            page={catalogPage}
            totalPages={catalogTotalPages}
            onPageChange={setCatalogPage}
          />
        ) : null}

        {adPost ? (
          <ProductPostingAdDialog
            open
            onOpenChange={(o) => {
              if (!o) setAdPost(null);
            }}
            platform={adPost.platform}
            headline={adPost.headline}
            productImages={resolveCatalogProduct(adPost.productId)?.productImages ?? []}
            price={adPost.price}
            location={adPost.location}
            description={adPost.description}
            minSalePricePkr={(() => {
              const p = resolveCatalogProduct(adPost.productId);
              const cur = p?.purchaseCurrency ?? "PKR";
              const base = p?.purchasePrice;
              if (cur && cur !== "PKR") return null;
              return base != null && Number.isFinite(Number(base)) && Number(base) > 0 ? Number(base) : null;
            })()}
            existingListing={
              adPost.existingListing
                ? {
                    id: adPost.existingListing.id,
                    salePrice: adPost.existingListing.salePrice,
                    images: adPost.existingListing.images,
                    platformLink: adPost.existingListing.platformLink,
                    createdAt: adPost.existingListing.createdAt,
                  }
                : null
            }
            onConfirm={async ({ salePrice, images }) => {
              const label = PRODUCT_POSTING_PLATFORM_LABEL[adPost.platform];
              try {
                if (adPost.existingListing) {
                  await updateProductAdListing(adPost.existingListing.id, { salePrice, images });
                  toast.success(`Updated ${label} listing for “${adPost.headline}”.`);
                } else {
                  await createProductAdListing({
                    productId: adPost.productId,
                    platform: productPostingPlatformToApi(adPost.platform),
                    salePrice,
                    platformLink: null,
                    images,
                  });
                  toast.success(`Saved ${label} listing for “${adPost.headline}”.`);
                }
                try {
                  setProductAdListings(await fetchProductAdListings());
                } catch (e2) {
                  console.error(e2);
                }
              } catch (e) {
                console.error(e);
                toast.error(catalogProductErrorMessage(e));
                throw e;
              }
            }}
          />
        ) : null}

        <Dialog
          open={lightbox.open}
          onOpenChange={(open) => {
            if (!open) setLightbox({ open: false, productId: null, index: 0, zoom: 1 });
          }}
        >
          <DialogContent hideCloseButton className="max-w-[min(64rem,calc(100vw-1.5rem))] overflow-hidden p-0">
            {(() => {
              const pid = lightbox.productId;
              const p = pid ? resolveCatalogProduct(pid) : null;
              const images = (p?.productImages ?? []).filter((u) => Boolean(u.url));
              if (!p || images.length === 0) {
                return <div className="p-6 text-sm text-muted-foreground">No images found.</div>;
              }

              const idx = clamp(lightbox.index, 0, images.length - 1);
              const active = images[idx];
              const zoom = clamp(lightbox.zoom, 1, 3);
              const canPrev = idx > 0;
              const canNext = idx < images.length - 1;

              return (
                <div className="relative w-full max-w-full overflow-hidden bg-black">
                  <div className="absolute left-3 top-3 z-30 flex items-center gap-2">
                    <span className="rounded-full border border-white/15 bg-black/35 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
                      {p.name}
                    </span>
                    <span className="rounded-full border border-white/15 bg-black/35 px-3 py-1 text-[11px] font-semibold text-white/90 backdrop-blur-sm tabular-nums">
                      {idx + 1}/{images.length}
                    </span>
                  </div>

                  <div className="absolute right-3 top-3 z-30 flex items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="h-9 w-9 rounded-full border border-white/15 bg-black/35 text-white hover:bg-black/45"
                      onClick={() => setLightbox((s) => ({ ...s, zoom: clamp((s.zoom ?? 1) + 0.5, 1, 3) }))}
                      aria-label="Zoom in"
                      title="Zoom in"
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="h-9 w-9 rounded-full border border-white/15 bg-black/35 text-white hover:bg-black/45"
                      onClick={() => setLightbox((s) => ({ ...s, zoom: clamp((s.zoom ?? 1) - 0.5, 1, 3) }))}
                      aria-label="Zoom out"
                      title="Zoom out"
                    >
                      <ZoomOut className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="h-9 w-9 rounded-full border border-white/15 bg-black/35 text-white hover:bg-black/45"
                      onClick={() => setLightbox({ open: false, productId: null, index: 0, zoom: 1 })}
                      aria-label="Close"
                      title="Close"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="relative h-[min(72vh,44rem)] w-full">
                    <div
                      ref={lightboxViewportRef}
                      className={cn(
                        "absolute inset-0 flex items-center justify-center overflow-auto",
                        zoom > 1 ? "cursor-grab" : "cursor-default",
                      )}
                      onPointerDown={(e) => {
                        if (zoom <= 1) return;
                        const el = lightboxViewportRef.current;
                        if (!el) return;
                        dragRef.current = {
                          dragging: true,
                          startX: e.clientX,
                          startY: e.clientY,
                          scrollLeft: el.scrollLeft,
                          scrollTop: el.scrollTop,
                        };
                        try {
                          (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
                        } catch {
                          /* ignore */
                        }
                      }}
                      onPointerMove={(e) => {
                        const st = dragRef.current;
                        if (!st.dragging) return;
                        const el = lightboxViewportRef.current;
                        if (!el) return;
                        const dx = e.clientX - st.startX;
                        const dy = e.clientY - st.startY;
                        el.scrollLeft = st.scrollLeft - dx;
                        el.scrollTop = st.scrollTop - dy;
                      }}
                      onPointerUp={(e) => {
                        if (!dragRef.current.dragging) return;
                        dragRef.current.dragging = false;
                        try {
                          (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
                        } catch {
                          /* ignore */
                        }
                      }}
                      onPointerCancel={() => {
                        dragRef.current.dragging = false;
                      }}
                    >
                      <img
                        src={active.url!}
                        alt={active.filename || p.name}
                        className="max-h-full max-w-full select-none object-contain"
                        style={{ transform: `scale(${zoom})`, transformOrigin: "center center" }}
                        draggable={false}
                      />
                    </div>

                    {images.length > 1 ? (
                      <>
                        <button
                          type="button"
                          className={cn(
                            "absolute left-2 top-1/2 z-40 -translate-y-1/2 rounded-full border border-white/15 bg-black/55 p-2 text-white shadow-lg backdrop-blur-sm transition-colors hover:bg-black/70",
                            !canPrev && "pointer-events-none opacity-40",
                          )}
                          onClick={() => setLightbox((s) => ({ ...s, index: clamp(s.index - 1, 0, images.length - 1), zoom: 1 }))}
                          aria-label="Previous image"
                          title="Previous"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          className={cn(
                            "absolute right-2 top-1/2 z-40 -translate-y-1/2 rounded-full border border-white/15 bg-black/55 p-2 text-white shadow-lg backdrop-blur-sm transition-colors hover:bg-black/70",
                            !canNext && "pointer-events-none opacity-40",
                          )}
                          onClick={() => setLightbox((s) => ({ ...s, index: clamp(s.index + 1, 0, images.length - 1), zoom: 1 }))}
                          aria-label="Next image"
                          title="Next"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>
                      </>
                    ) : null}
                  </div>

                  {images.length > 1 ? (
                    <div className="w-full min-w-0 overflow-hidden border-t border-white/10 bg-black/70 p-3">
                      <div className="flex w-full min-w-0 gap-2 overflow-x-auto pb-1">
                        {images.map((img, j) => (
                          <button
                            key={`${img.id}-${j}`}
                            type="button"
                            className={cn(
                              "relative h-14 w-20 shrink-0 overflow-hidden rounded-md border",
                              j === idx ? "border-primary" : "border-white/15 hover:border-white/35",
                            )}
                            onClick={() => setLightbox((s) => ({ ...s, index: j, zoom: 1 }))}
                            aria-label={`Open image ${j + 1}`}
                          >
                            <img src={img.url!} alt="" className="h-full w-full object-cover" loading="lazy" />
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={publishConfirmProduct !== null}
          onOpenChange={(o) => !o && setPublishConfirmProduct(null)}
          title="Publish this product?"
          description={
            publishConfirmProduct ? (
              <>
                <span className="font-medium text-foreground">{publishConfirmProduct.name}</span> will be set to{" "}
                <strong className="text-foreground">In stock</strong> and shown as available inventory.
              </>
            ) : null
          }
          mode="confirm"
          variant="warning"
          confirmLabel="Publish"
          cancelLabel="Cancel"
          onConfirm={() => {
            const p = publishConfirmProduct;
            if (!p) return;
            setPublishConfirmProduct(null);
            void handlePublishToInStock(p);
          }}
        />

        <Dialog
          open={copyConfirmProduct !== null}
          onOpenChange={(o) => {
            if (!o) setCopyConfirmProduct(null);
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Copy this product?</DialogTitle>
            </DialogHeader>
            {copyConfirmProduct ? (
              <div className="space-y-4 py-1">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Choose which parts of{" "}
                  <span className="font-medium text-foreground">{copyConfirmProduct.name}</span> to include in the
                  draft copy.
                </p>
                <div className="space-y-2.5 rounded-xl border border-border/50 bg-muted/10 p-3">
                  {(
                    [
                      {
                        key: "basic" as const,
                        label: "Basic information",
                        hint: "Name, category, make/model, location",
                        locked: true,
                        show: true,
                      },
                      {
                        key: "additional" as const,
                        label: "Additional Information",
                        hint: "Specs and attribute fields",
                        locked: false,
                        show: true,
                      },
                      {
                        key: "media" as const,
                        label: "Images & documents",
                        hint: "Gallery photos and uploaded files",
                        locked: false,
                        show: true,
                      },
                      {
                        key: "purchase" as const,
                        label: "Purchase & landed cost",
                        hint: "Costs, pricing, deal partners",
                        locked: false,
                        show: canViewPrices,
                      },
                    ] as const
                  )
                    .filter((opt) => opt.show)
                    .map((opt) => (
                    <label
                      key={opt.key}
                      htmlFor={`copy-section-${opt.key}`}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border border-border/40 bg-background/60 px-3 py-2.5 cursor-pointer",
                        opt.locked && "opacity-90",
                      )}
                    >
                      <Checkbox
                        id={`copy-section-${opt.key}`}
                        checked={copySections[opt.key]}
                        disabled={opt.locked || copyBusyId != null}
                        onCheckedChange={(c) => {
                          if (opt.locked) return;
                          setCopySections((s) => ({ ...s, [opt.key]: c === true }));
                        }}
                        className="mt-0.5"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-foreground">
                          {opt.label}
                          {opt.locked ? (
                            <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                              Required
                            </span>
                          ) : null}
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5">{opt.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ) : null}
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="glass"
                disabled={copyBusyId != null}
                onClick={() => setCopyConfirmProduct(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="premium"
                disabled={copyBusyId != null || !copyConfirmProduct}
                onClick={() => {
                  if (!copyConfirmProduct) return;
                  void handleCopyAsDraft(copyConfirmProduct);
                }}
              >
                {copyBusyId != null ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Copying...
                  </>
                ) : (
                  "Copy as draft"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={deleteTargetId !== null}
          onOpenChange={(o) => !o && setDeleteTargetId(null)}
          title="Delete this product?"
          description="This will remove the product from your catalog. This action cannot be undone."
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={async () => {
            if (!deleteTargetId) return;
            if (!canDelete) {
              toast.error("You don’t have permission to delete products.");
              setDeleteTargetId(null);
              return;
            }
            try {
              await deleteProduct(deleteTargetId);
              setDeleteTargetId(null);
              toast.success("Product deleted successfully.");
              refetchCatalog();
              setVersion((v) => v + 1);
            } catch (e) {
              console.error(e);
              toast.error(catalogProductErrorMessage(e));
            }
          }}
        />

        <Dialog
          open={statusModalProduct !== null}
          onOpenChange={(open) => {
            if (statusSaving) return;
            if (!open) {
              setStatusModalProduct(null);
              setNextInventoryStatus("");
              setStatusReason("");
            }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Change Inventory Status</DialogTitle>
              {statusModalProduct ? (
                <DialogDescription>
                  Product: <span className="font-medium text-foreground">{statusModalProduct.name}</span>
                  <br />
                  Current status:{" "}
                  <span className="font-medium text-foreground">
                    {inventoryCardStatusPill(statusModalProduct.inventoryStatus).label}
                  </span>
                </DialogDescription>
              ) : null}
            </DialogHeader>
            <div className="space-y-4 py-1">
              <div className="space-y-2">
                <Label htmlFor="inventory-status-select-page">New Inventory Status</Label>
                <Select
                  value={nextInventoryStatus || undefined}
                  onValueChange={(v) => setNextInventoryStatus(v as InventoryStatusValue)}
                  disabled={statusSaving}
                >
                  <SelectTrigger id="inventory-status-select-page" className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {INVENTORY_STATUS_FILTER_OPTS.filter(
                      (opt) => opt.value !== (statusModalProduct?.inventoryStatus ?? "draft"),
                    ).map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="inventory-status-reason-page">
                  Reason <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Textarea
                  id="inventory-status-reason-page"
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  placeholder="Why is the status changing?"
                  rows={3}
                  disabled={statusSaving}
                  className="resize-y"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                disabled={statusSaving}
                onClick={() => setStatusModalProduct(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={statusSaving || !nextInventoryStatus || !statusModalProduct}
                onClick={() => {
                  if (!nextInventoryStatus || !statusModalProduct) return;
                  void (async () => {
                    setStatusSaving(true);
                    try {
                      await changeProductInventoryStatus(
                        statusModalProduct.id,
                        nextInventoryStatus,
                        statusReason,
                      );
                      setStatusModalProduct(null);
                      setNextInventoryStatus("");
                      setStatusReason("");
                      toast.success("Inventory status updated.");
                      refetchCatalog();
                    } catch (e) {
                      toast.error(catalogProductErrorMessage(e));
                    } finally {
                      setStatusSaving(false);
                    }
                  })();
                }}
              >
                {statusSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save status"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Products;
